import SearchSuggestion from '../../elements/searchSuggestion';
import delegate from 'delegate';

export default class HeaderSearchController {
    constructor() {
        this.form = null;
        this.formInput = null;
        this.searchResultsContainer = null;
        this.searchBackground = null;
        this.header = null;

        this.apiEndpoint = '/umbraco/api/search/GetSuggestions?filter=';

        this.nodeId = document.body.getAttribute('nodeid');
        this.culture = document.body.getAttribute('culture');

        this.abortController = null; // Will contain an AbortController when making a new API call
        this.waiterTime = 250; // Time in MS to wait with making a new API call
        this.changeWaiter = null; // Clearable timeout handler

        this.originalSearchSuggestions = [];
        this.originalSearchTitle = '';

        if (document.querySelector('form.headerSearchForm')) {
            this.init();
        }
    }

    init() {
        this.form = document.querySelector('form.headerSearchForm');
        this.formInput = this.form.querySelector('input[name="q"]');
        this.searchResultsContainer = document.querySelector('section.headerSearchResults');
        this.searchBackground = document.querySelector('div.headerSearchResultsBackground');
        this.header = document.querySelector('header');
        this.abortController = new AbortController();

        // Make a copy of the search suggestions for restore ability
        this.searchResultsContainer.querySelectorAll('ul li').forEach(li => this.originalSearchSuggestions.push(li.cloneNode(true)));

        // Copy original title
        this.originalSearchTitle = this.searchResultsContainer.querySelector('#searchResultsTitle').innerHTML;

        this.setEventListeners();
    }

    setEventListeners() {
        // Opening
        this.formInput.addEventListener('focus', this.showSearchResults.bind(this));
        this.formInput.addEventListener('click', this.showSearchResults.bind(this));
        if (document.querySelector('.headerNavigation a.searchButton')) {
            document.querySelector('.headerNavigation a.searchButton').addEventListener('click', () => {
                if (document.querySelector('body > header').classList.contains('searchActive')) {
                    this.hideSearchResults();
                } else {
                    this.showSearchResults();
                    this.formInput.focus();
                }
            });
        }

        // Changing
        this.formInput.addEventListener('input', this.handleInputChange.bind(this));

        // Submitting
        this.searchResultsContainer.querySelector('.status a').addEventListener('click', () => this.form.submit());
        this.form.addEventListener('submit', e => {
            if (!this.formInput.value.trim()) {
                e.preventDefault();
                this.formInput.focus();
                return false;
            }
            return true;
        });

        // Closing 
        this.searchBackground.addEventListener('click', this.hideSearchResults.bind(this));

        // Werkt niet omdat deze niet wordt afgevuurd wanneer ander component focus heeft.
        this.header.addEventListener('focusout', (e) => {
            if (!(this.form.contains(e.relatedTarget)) && !(this.searchResultsContainer.contains(e.relatedTarget))) {
                console.log('closing');
                this.hideSearchResults();
            };
        });

        // Others
        document.addEventListener('keydown', e => {
			if (e.key.toLowerCase().substr(0, 3) == 'esc' && this.searchBackground.classList.contains('active')) {
                this.hideSearchResults();
                this.formInput.blur();
            }
        });

        this.formInput.addEventListener('keydown', e => {
            if (e.key.toLowerCase() == 'arrowdown') {
                e.preventDefault();
                this.searchResultsContainer.querySelector('ul li a').focus();
            }
        });

        delegate('section.headerSearchResults ul li a', 'keydown', e => {
            if (e.key.toLowerCase() == 'arrowup') {
                e.preventDefault();
                this.arrowUp(e.delegateTarget.parentNode);
            } else if (e.key.toLowerCase() == 'arrowdown') {
                e.preventDefault();
                this.arrowDown(e.delegateTarget.parentNode);
            }
        });
    }

    /**
     * @param {HTMLElement} li 
     */
    arrowDown(li) {
        if (li.nextElementSibling) {
            li.nextElementSibling.querySelector('a').focus();
        }
    }

    /**
     * @param {HTMLElement} li 
     */
    arrowUp(li) {
        if (li.previousElementSibling) {
            li.previousElementSibling.querySelector('a').focus();
        } else {
            this.formInput.focus();
        }
    }

    handleInputChange() {
        let query = this.formInput.value.trim();

        if (query) {
            // Update status button to show
            this.searchResultsContainer.querySelector('.status a').classList.remove('inactive');
            this.searchResultsContainer.querySelector('.status a').title = `Zoek op de term "${query}"`;

            this.updateResults(query);
        } else {
            this.searchResultsContainer.querySelector('.status a').classList.add('inactive');

            this.resetResults();
        }
    }

    showSearchResults() {
        document.querySelector('body > header').classList.add('searchActive');
        this.searchResultsContainer.classList.add('active');
        this.searchBackground.classList.add('active');

        this.searchResultsContainer.setAttribute('aria-expanded', true);
        this.searchResultsContainer.setAttribute('aria-hidden', false);

        document.documentElement.classList.add('disableScroll');

        let searchCounter = 3;

        // Enable tabbing on all items
        this.searchResultsContainer.querySelectorAll('a, button').forEach(clickable => {
            searchCounter++;
            clickable.setAttribute('tabindex', searchCounter);
            clickable.setAttribute('aria-hidden', 'false');
        });
    }

    hideSearchResults() {
        document.querySelector('body > header').classList.remove('searchActive');
        this.searchResultsContainer.classList.remove('active');
        this.searchBackground.classList.add('disappearing');

        this.searchResultsContainer.setAttribute('aria-expanded', false);
        this.searchResultsContainer.setAttribute('aria-hidden', true);

        document.documentElement.classList.remove('disableScroll');

        setTimeout(() => {
            if (!this.searchResultsContainer.classList.contains('active')) {
                this.searchBackground.classList.remove('active');
                this.resetResults();
            }
            this.searchBackground.classList.remove('disappearing');
        }, 400);

        // Disable tabbing on all items
        this.searchResultsContainer.querySelectorAll('a, button').forEach(clickable => {
            clickable.setAttribute('tabindex', '-1');
            clickable.setAttribute('aria-hidden', 'true');
        });
    }

    updateResults(query) {
        // Reset waiter to prevent unnessecary calls
        if (this.changeWaiter !== null) {
            clearTimeout(this.changeWaiter);
        }

        this.changeWaiter = setTimeout(() => {
            this.searchResultsContainer.querySelector('ul').classList.add('loading');

            // If there's a call going on, abort it and create a new abortcontroller to reuse
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = new AbortController();
            }

            // Fetch new results
            this.fetchResults(query).then(data => {
                this.insertSearchSuggestions(data);
            }).catch(e => {
                console.error(e);
            }).finally(() => {
                this.searchResultsContainer.querySelector('ul').classList.remove('loading');
            });

            // TESTING PURPOSES: fake data
            // this.testResults(query).then(data => {
            //     this.insertSearchSuggestions(data);

            //     this.searchResultsContainer.querySelector('ul').classList.remove('loading');
            // });
            
            this.changeWaiter = null;
        }, this.waiterTime);
    }

    insertSearchSuggestions(data) {
        // Data should be an array containing nothing but keywords. So create listitems that will search for that
        let suggestions = [];
        if (data.Suggestions) {
            data.Suggestions.forEach(item => {
                let suggestion = new SearchSuggestion({
                    name: item.Title,
                    url: item.Url
                });
    
                suggestions.push(suggestion.generate());
            });
        }        

        // Clear current suggestion list
        this.searchResultsContainer.querySelector('ul').innerHTML = '';
        
        // Insert the newly made suggestions
        suggestions.forEach(suggestion => this.searchResultsContainer.querySelector('ul').appendChild(suggestion));

        // Add tabindex priority to results
        let searchCounter = 3; // begin at 3 for logo, searchinput and searchsubmit
        if(this.searchResultsContainer.querySelectorAll('ul li').length > 0){
            this.searchResultsContainer.querySelectorAll('ul li').forEach(listitem => {
            let clickableItem = listitem.querySelector('a, button');
            searchCounter++;
            clickableItem.setAttribute('tabindex', parseInt(searchCounter));
            })
        }
        
        // Update title
        this.searchResultsContainer.querySelector('#searchResultsTitle').innerHTML = `${data.TotalResults == 0 ? 'Geen' : data.TotalResults} ${data.TotalResults == 1 ? 'resultaat' : 'resultaten'}`;
    }

    resetResults() {
        // Cancel any ongoing call
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = new AbortController();
        }

        // Clear input
        this.formInput.value = '';

        // Clear suggestion list
        this.searchResultsContainer.querySelector('ul').innerHTML = '';
        
        // Insert backed up suggestions and title
        this.originalSearchSuggestions.forEach(suggestion => this.searchResultsContainer.querySelector('ul').appendChild(suggestion));
        this.searchResultsContainer.querySelector('#searchResultsTitle').innerHTML = this.originalSearchTitle;
        this.searchResultsContainer.querySelector('ul').classList.remove('loading');
    }

    fetchResults(query) {
        return fetch(`${this.apiEndpoint}${encodeURIComponent(query)}`, {
            signal: this.abortController.signal,
            headers: {
                "HD_UMB_NodeId": this.nodeId,
                "HD_UMB_Culture": this.culture
            }
        }).then(response => {
            return response.json();
        });
    }

    testResults(query) {
        const randomUrl = () => {
            return ['/', '/contact/', '/uw-bezoek/bij-iemand-op-bezoek/', '/cookies/', '/uw-zorgspecialist/', '/wachttijden/'][Math.floor(Math.random() * (6 - 0 + 1) + 0)];
        }

        return new Promise(resolve => {
            setTimeout(() => {
                resolve({
                    TotalResults: Math.floor(Math.random() * (200 - 5 + 1) + 5),
                    Items: [
                        {
                            Title: `Testresultaat van ${query} 1`,
                            Url: randomUrl()
                        },
                        {
                            Title: `Testresultaat van ${query} 2`,
                            Url: randomUrl()
                        },
                        {
                            Title: `Testresultaat van ${query} 3`,
                            Url: randomUrl()
                        },
                        {
                            Title: `Testresultaat van ${query} 4`,
                            Url: randomUrl()
                        },
                        {
                            Title: `Testresultaat van ${query} 5`,
                            Url: randomUrl()
                        },
                    ]
                });
            }, Math.floor(Math.random() * (800 - 200 + 1) + 200));
        });
    }
}